import { useState, useEffect } from "react";
import { X, Key, Globe, Lock, Eye, EyeOff, Shield, Clock } from "lucide-react";
import styles from "./ApiCrendtialsDetail.module.css";
import { formatDateTime } from "../../utils/dateHelpers";
import { useCredentialById } from "../../api/suppliers/useCredentialsApi";

const ApiCrendtialsDetail = ({ isOpen, onClose, credential }) => {
  const [showApiKey, setShowApiKey] = useState(false);
  const [showApiSecret, setShowApiSecret] = useState(false);
  const credentialId = credential?.id || credential?._raw?.credentialSetId || credential?._raw?.id;
  const { data: fullCredential, loading } = useCredentialById(credentialId && isOpen ? credentialId : null);
  const displayCredential = fullCredential || credential?._raw || credential;
  const baseUrl = displayCredential?.baseUrl || (credential?.supplier ? `https://api.${String(credential.supplier).toLowerCase().replace(/\s+/g, "")}.com/v2` : "");

  useEffect(() => {
    if (!isOpen) {
      setShowApiKey(false);
      setShowApiSecret(false);
    }
  }, [isOpen]);

  if (!isOpen || !credential) return null;

  return (
    <>
      <div className={styles.backdrop} onClick={onClose} />
      <div className={styles.drawer}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.iconWrapper}>
              <Key size={24} />
            </div>
            <div>
              <h2 className={styles.title}>API Credential Details</h2>
              <p className={styles.subtitle}>
                View and manage API authentication configuration
              </p>
            </div>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className={styles.content}>
          {/* Basic Information */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Basic Information</h3>
            <div className={styles.grid}>
              <div className={styles.infoCard}>
                <label className={styles.cardLabel}>Credential Set ID</label>
                <div className={styles.cardValue}>{displayCredential?.credentialSetId || credential?.id}</div>
              </div>
              <div className={styles.infoCard}>
                <label className={styles.cardLabel}>Supplier</label>
                <div className={styles.cardValue}>{displayCredential?.supplierName || credential?.supplier}</div>
              </div>
              <div className={styles.infoCard}>
                <label className={styles.cardLabel}>Environment</label>
                <div className={`${styles.badge} ${styles[`env${displayCredential?.environment || credential?.environment}`]}`}>
                  {displayCredential?.environment || credential?.environment}
                </div>
              </div>
              <div className={styles.infoCard}>
                <label className={styles.cardLabel}>Status</label>
                <div
                  className={`${styles.statusBadge} ${
                    (displayCredential?.status || credential?.status) === "Active"
                      ? styles.statusActive
                      : styles.statusInactive
                  }`}
                >
                  {displayCredential?.status || credential?.status}
                </div>
              </div>
            </div>
          </section>

          {/* Base URL */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <Globe size={16} />
              Base URL
            </h3>
            <div className={styles.field}>
              <label className={styles.label}>API Endpoint</label>
              <div className={styles.inputWrapper}>
                <input
                  type="text"
                  value={loading ? "Loading..." : baseUrl}
                  readOnly
                  className={styles.input}
                />
              </div>
            </div>
          </section>

          {/* Authentication Configuration */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <Lock size={16} />
              Authentication Configuration
            </h3>
            <div className={styles.field}>
              <label className={styles.label}>Authentication Type</label>
              <select className={styles.select} value={displayCredential?.authenticationType || credential?.authType} readOnly disabled>
                <option>API Key</option>
                <option>OAuth2</option>
                <option>Basic Auth</option>
                <option>Custom</option>
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.label}>API Key</label>
              <div className={styles.passwordField}>
                <input
                  type={showApiKey ? "text" : "password"}
                  value={fullCredential?.apiKey ? (showApiKey ? fullCredential.apiKey : "••••••••••••") : "—"}
                  readOnly
                  className={styles.passwordInput}
                />
                <button
                  className={styles.toggleBtn}
                  onClick={() => setShowApiKey(!showApiKey)}
                >
                  {showApiKey ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className={styles.field}>
              <label className={styles.label}>API Secret</label>
              <div className={styles.passwordField}>
                <input
                  type={showApiSecret ? "text" : "password"}
                  value={fullCredential?.apiSecret ? (showApiSecret ? fullCredential.apiSecret : "••••••••••••") : "—"}
                  readOnly
                  className={styles.passwordInput}
                />
                <button
                  className={styles.toggleBtn}
                  onClick={() => setShowApiSecret(!showApiSecret)}
                >
                  {showApiSecret ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </section>

          {/* Security Notice */}
          <div className={styles.alert}>
            <Shield size={20} className={styles.alertIcon} />
            <div className={styles.alertContent}>
              <div className={styles.alertTitle}>Security Notice</div>
              <div className={styles.alertText}>
                All credential access is logged. Never share credentials outside
                authorized systems.
              </div>
            </div>
          </div>

          {/* Last Updated Footer */}
          <div className={styles.updateInfo}>
            <Clock size={14} />
            <span>Last updated: {formatDateTime(displayCredential?.updatedAt || credential?.updatedAt) || "—"}</span>
          </div>
        </div>

        <div className={styles.footer}>
          <button className={styles.closeFooterBtn} onClick={onClose}>
            <X size={16} />
            Close
          </button>
          <button className={styles.saveBtn}>
            <Shield size={16} />
            Save Changes
          </button>
        </div>
      </div>
    </>
  );
};


export default ApiCrendtialsDetail;